import { Link, useLocation } from 'react-router-dom';
import { Home, Library, User, Compass, LogOut, Crown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export const Navigation = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { path: '/dashboard', icon: Home, label: 'Home' },
    { path: '/library', icon: Compass, label: 'Explore' },
    { path: '/pricing', icon: Crown, label: 'Pricing' },
    { path: '/profile', icon: User, label: 'Profile' },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 glass-strong md:top-0 md:bottom-auto">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo - Desktop only */}
          <Link 
            to="/" 
            className="hidden md:flex items-center gap-3 group"
            data-testid="nav-logo"
          >
            <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center">
              <span className="font-serif text-xl text-white">L</span>
            </div>
            <span className="font-serif text-xl text-zinc-100 group-hover:text-white transition-colors">
              Lifetunes
            </span>
          </Link>

          {/* Navigation Items */}
          <div className="flex items-center justify-around w-full md:w-auto md:gap-8">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`
                    flex flex-col md:flex-row items-center gap-1 md:gap-2 px-4 py-2 rounded-full
                    transition-colors duration-200
                    ${active 
                      ? 'text-white md:bg-white/10' 
                      : 'text-zinc-500 hover:text-zinc-300'
                    }
                  `}
                >
                  <Icon 
                    className={`w-5 h-5 ${active ? 'text-violet-400' : ''}`} 
                    strokeWidth={active ? 2 : 1.5}
                  />
                  <span className="text-xs md:text-sm font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Right side - Desktop only */}
          <div className="hidden md:flex items-center gap-4">
            {user && (
              <>
                <span className="text-xs text-zinc-500" data-testid="nav-user-email">{user.email}</span>
                <button
                  onClick={logout}
                  className="flex items-center gap-2 text-zinc-500 hover:text-red-400 transition-colors text-sm"
                  data-testid="nav-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            )}
            <Link
              to="/library"
              className="flex items-center gap-2 bg-white text-zinc-900 px-6 py-2.5 rounded-full font-medium text-sm hover:bg-zinc-200 transition-colors"
              data-testid="nav-start-btn"
            >
              <Library className="w-4 h-4" />
              Start Session
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
